/**
 * BenefitAduaneroSelect — B-02B / CA-07
 * Selector unificado de Régimen Aduanero por ítem (reemplaza el toggle legacy `aplica_tlc`).
 *
 * Persiste `beneficio_aduanero` en `base_inventario` y mantiene `aplica_tlc`
 * sincronizado (= value !== 'GENERAL') por compatibilidad con el código legacy.
 */
import { useState } from "react";
import { Info, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type BenefitValue = "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA";

const OPTIONS: { value: BenefitValue; label: string }[] = [
  { value: "GENERAL", label: "General (sin beneficio)" },
  { value: "TLC_PURO", label: "TLC / Acuerdo Comercial (arancel 0%)" },
  { value: "LEY_1715", label: "Ley 1715 — Energías Renovables (arancel + IVA 0%)" },
  { value: "ZONA_FRANCA", label: "Zona Franca (arancel + IVA suspendidos)" },
];

const TOOLTIP_TEXT =
  "Selecciona el régimen que aplica según la DIM y el Certificado de Origen. Ley 1715 requiere certificado UPME vigente.";

interface Props {
  /** Requerido cuando `mode="persist"`. Ignorado en modo `controlled`. */
  itemId?: string;
  /** Valor actual; si null/undefined, cae a 'GENERAL'. Acepta back-compat con `aplica_tlc`. */
  value: BenefitValue | null | undefined;
  /** Flag legacy para inferir TLC_PURO si `value` viene null. */
  legacyAplicaTlc?: boolean | null;
  /** Variantes de presentación. `compact` se usa dentro de tabla; `full` en formularios. */
  variant?: "compact" | "full";
  className?: string;
  /**
   * Fase 1.3 — Modo de operación:
   *   - `persist`  (default): escribe en `base_inventario` (uso clásico).
   *   - `controlled`: NO toca DB, solo emite `onChange`. Útil para simuladores
   *     y otros escenarios efímeros donde el padre maneja el estado.
   */
  mode?: "persist" | "controlled";
  /** Callback obligatorio en modo `controlled`. Recibe el nuevo régimen. */
  onChange?: (next: BenefitValue) => void;
  disabled?: boolean;
}

export function resolveBenefitValue(
  value: BenefitValue | null | undefined,
  legacyAplicaTlc?: boolean | null,
): BenefitValue {
  if (value && value !== "GENERAL") return value;
  if (value === "GENERAL") return "GENERAL";
  return legacyAplicaTlc ? "TLC_PURO" : "GENERAL";
}

export function BenefitAduaneroSelect({
  itemId,
  value,
  legacyAplicaTlc,
  variant = "full",
  className,
  mode = "persist",
  onChange,
  disabled,
}: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const current = resolveBenefitValue(value, legacyAplicaTlc);

  const handleChange = async (next: string) => {
    const nextVal = next as BenefitValue;
    if (nextVal === current) return;

    // ── Modo controlled: solo emite, no persiste ─────────────────────────
    if (mode === "controlled") {
      onChange?.(nextVal);
      return;
    }

    // ── Modo persist (default): escribe en base_inventario ───────────────
    if (!itemId) {
      console.warn("[BenefitAduaneroSelect] mode='persist' requiere itemId");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("base_inventario")
      .update({
        beneficio_aduanero: nextVal,
        // Sync legacy flag for back-compat with downstream code that still reads aplica_tlc.
        aplica_tlc: nextVal !== "GENERAL",
      } as any)
      .eq("id", itemId);
    setSaving(false);
    if (error) {
      toast({
        title: "No se pudo actualizar el régimen aduanero",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
    onChange?.(nextVal);
    toast({
      title: "✅ Régimen actualizado",
      description: OPTIONS.find((o) => o.value === nextVal)?.label,
    });
  };

  const label = (
    <div className="flex items-center gap-1">
      <Label className="text-xs">Régimen / Beneficio Aduanero</Label>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="inline-flex" aria-label="Información de régimen aduanero">
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{TOOLTIP_TEXT}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  return (
    <div className={className}>
      {variant === "full" && label}
      <div className="flex items-center gap-1">
        <Select value={current} onValueChange={handleChange} disabled={saving || disabled}>
          <SelectTrigger
            className={
              variant === "compact"
                ? "h-7 text-[11px] px-2 min-w-[140px]"
                : "h-9 text-xs"
            }
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[100]">
            {OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-xs">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>
    </div>
  );
}

/** Badge de solo lectura para tablas de inventario. GENERAL → null (sin badge). */
export function BenefitAduaneroBadge({
  value,
  legacyAplicaTlc,
}: {
  value: BenefitValue | null | undefined;
  legacyAplicaTlc?: boolean | null;
}) {
  const current = resolveBenefitValue(value, legacyAplicaTlc);
  if (current === "GENERAL") return null;
  const map: Record<Exclude<BenefitValue, "GENERAL">, { label: string; cls: string }> = {
    TLC_PURO: {
      label: "TLC",
      cls:
        "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800",
    },
    LEY_1715: {
      label: "Ley 1715",
      cls:
        "bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800",
    },
    ZONA_FRANCA: {
      label: "Z. Franca",
      cls:
        "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800",
    },
  };
  const c = map[current];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold leading-none ${c.cls}`}
    >
      {c.label}
    </span>
  );
}
